import React, { useEffect, useRef } from "react"
import * as Chart from "chart.js"
import { translations } from "../../../utils/Translation/staticTranslation"

Chart.Chart.register(...Chart.registerables)

const MerAnalyticsCharts = ({ data }) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.MerchantAnalytics || {}
  const mainChartRef = useRef(null)
  const donutChartRef = useRef(null)
  const mainChartInstance = useRef(null)
  const donutChartInstance = useRef(null)

  useEffect(() => {
    // ✅ Plugin: dashed horizontal grid lines under area fills
    const dashedHorizontalGrid = {
      id: "dashedHorizontalGrid",
      beforeDatasetsDraw(chart) {
        const { ctx, scales } = chart
        const y = scales.y
        const x = scales.x
        if (!y || !x) return
        ctx.save()
        ctx.setLineDash([6, 4])
        ctx.strokeStyle = "#E5E7EB"
        ctx.lineWidth = 1
        y.ticks.forEach((_, i) => {
          const yPos = y.getPixelForTick(i)
          ctx.beginPath()
          ctx.moveTo(x.left, yPos)
          ctx.lineTo(x.right, yPos)
          ctx.stroke()
        })
        ctx.restore()
      },
    }

    // ✅ Plugin: vertical hover line + white-bordered circular dots
    const hoverLinePlugin = {
      id: "hoverLine",
      afterDraw(chart) {
        if (!chart.tooltip?._active?.length) return
        const ctx = chart.ctx
        const active = chart.tooltip._active[0]
        const x = active.element.x
        const topY = chart.scales.y.top
        const bottomY = chart.scales.y.bottom
        ctx.save()
        ctx.setLineDash([6, 4])
        ctx.beginPath()
        ctx.moveTo(x, topY)
        ctx.lineTo(x, bottomY)
        ctx.lineWidth = 1.2
        ctx.strokeStyle = "rgba(0,0,0,0.35)"
        ctx.stroke()
        ctx.restore()
        chart.data.datasets.forEach((dataset, i) => {
          const meta = chart.getDatasetMeta(i)
          const point = meta.data[active.index]
          if (!point) return
          ctx.save()
          ctx.beginPath()
          ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI)
          ctx.fillStyle = dataset.borderColor
          ctx.fill()
          ctx.lineWidth = 2
          ctx.strokeStyle = "#fff"
          ctx.stroke()
          ctx.restore()
        })
      },
    }

    // ===== Main Chart (replaces area chart) =====
    if (mainChartRef.current) {
      const ctx = mainChartRef.current.getContext("2d")
      if (mainChartInstance.current) mainChartInstance.current.destroy()

      // Process API data
      const monthlyData =
        data?.result?.monthly_clicks_views || data?.monthly_clicks_views || []

      // console.log("Monthly Data from API:", monthlyData)

      // Format month labels (e.g., "2025-09" -> "Sep")
      const monthNames =
        lang === "ar"
          ? [
              t.months?.jan || "يناير",
              t.months?.feb || "فبراير",
              t.months?.mar || "مارس",
              t.months?.apr || "أبريل",
              t.months?.may || "مايو",
              t.months?.jun || "يونيو",
              t.months?.jul || "يوليو",
              t.months?.aug || "أغسطس",
              t.months?.sep || "سبتمبر",
              t.months?.oct || "أكتوبر",
              t.months?.nov || "نوفمبر",
              t.months?.dec || "ديسمبر",
            ]
          : [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ]
      const formattedData = monthlyData.map((item) => {
        const monthIndex = parseInt(item.month_date.split("-")[1]) - 1
        return {
          month: monthNames[monthIndex],
          clicks: item.clicks,
          views: item.views,
        }
      })

      const gradientOrange = ctx.createLinearGradient(0, 0, 0, 400)
      gradientOrange.addColorStop(0, "rgba(255, 247, 188, 1)")
      gradientOrange.addColorStop(1, "rgba(255, 247, 188, 0.05)")

      const gradientBlue = ctx.createLinearGradient(0, 0, 0, 400)
      gradientBlue.addColorStop(0, "rgba(16, 153, 227, 0.78)")
      gradientBlue.addColorStop(1, "rgba(16, 153, 227, 0)")

      const gradientGreen = ctx.createLinearGradient(0, 0, 0, 400)
      gradientGreen.addColorStop(0, "rgba(191, 9, 61, 0.8)")
      gradientGreen.addColorStop(1, "rgba(191, 9, 61, 0)")

      const labels = formattedData.map((d) => d.month)
      const clicksData = formattedData.map((d) => d.clicks)
      const viewsData = formattedData.map((d) => d.views)

      console.log("Chart Data:", {
        labels,
        clicksData,
        viewsData,
        formattedData,
      })

      mainChartInstance.current = new Chart.Chart(ctx, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: t.charts?.clicks || "Clicks",
              data: clicksData,
              borderColor: "rgba(16, 153, 227, 1)",
              backgroundColor: gradientBlue,
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointRadius: 0,
            },
            {
              label: t.charts?.views || "Views",
              data: viewsData,
              borderColor: "rgba(191, 9, 61, 1)",
              backgroundColor: gradientGreen,
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointRadius: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: {
              display: false,
              labels: {
                usePointStyle: true,
                color: "#374151",
                font: { size: 13 },
              },
            },
            tooltip: {
              enabled: true,
              backgroundColor: "rgba(17,24,39,0.85)",
              titleFont: { size: 13 },
              bodyFont: { size: 12 },
              padding: 10,
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: "#6B7280" },
              border: { display: false },
            },
            y: {
              beginAtZero: true,
              grid: { display: false },
              ticks: { color: "#6B7280" },
              border: { display: false },
            },
          },
        },
        plugins: [dashedHorizontalGrid, hoverLinePlugin],
      })
    }

    // ===== Donut Chart (unchanged) =====
    if (donutChartRef.current) {
      const ctx = donutChartRef.current.getContext("2d")
      if (donutChartInstance.current) donutChartInstance.current.destroy()
      donutChartInstance.current = new Chart.Chart(ctx, {
        type: "doughnut",
        data: {
          labels: [
            t.charts?.free || "Free",
            t.charts?.standard || "Standard",
            t.charts?.enterprise || "Enterprise",
          ],
          datasets: [
            {
              data: [10, 0, 0],
              backgroundColor: ["#9CA3AF", "#6366F1", "#F97316"],
              borderWidth: 0,
              cutout: "75%",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: "rgba(0,0,0,0.8)",
              padding: 12,
              titleColor: "#fff",
              bodyColor: "#fff",
              callbacks: {
                label(context) {
                  const label = context.label || ""
                  const value = context.parsed
                  const total = context.dataset.data.reduce((a, b) => a + b, 0)
                  const percent = ((value / total) * 100).toFixed(0)
                  return `${label}: ${value.toLocaleString()} (${percent}%)`
                },
              },
            },
          },
        },
      })
    }

    return () => {
      mainChartInstance.current?.destroy()
      donutChartInstance.current?.destroy()
    }
  }, [data])

  return (
    <div className=" bg-light">
      <div className="analytics-overview-main  justify-content-center">
        {/* Analytics Overview */}
        <div
          className={
            lang === "ar" ? "overview-chart-mer-left" : "overview-chart-mer"
          }
        >
          <div className="card shadow-sm border-0 rounded-4 p-4">
            <h5 className="fw-semibold text-dark mb-1">
              {t.charts?.analytics_overview || "Analytics Overview"}
            </h5>
            <p className="text-secondary small mb-4">
              {t.charts?.performance_metrics_over_time ||
                "Performance metrics over time"}
            </p>

            <div style={{ height: "340px" }}>
              <canvas ref={mainChartRef}></canvas>
            </div>

            <hr className="mt-4" />

            <div className="row  mt-3">
              <div className="col ">
                <h4 className="fw-bold mb-0" style={{ color: "#1099E3" }}>
                  {data?.result?.total_clicks}
                </h4>
                <small className="text-secondary">
                  {t.charts?.total_clicks || "Total Clicks"}
                </small>
              </div>
              <div className="col text-center">
                <h4 className="fw-bold mb-0" style={{ color: "#BF093D" }}>
                  {data?.result?.total_impressions}
                </h4>
                <small className="text-secondary">
                  {t.charts?.total_impressions || "Total Impressions"}
                </small>
              </div>
              <div className="col text-end">
                <h4 className="fw-bold mb-0" style={{ color: "#AC7F5E" }}>
                  {/* $751K */}
                </h4>
                {/* <small className="text-secondary">Revenue</small> */}
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Plans */}
        <div className="donut-chart">
          <div className="card shadow-sm border-0 rounded-4 p-4 ">
            <div className="merchat-charts-heading">
              {t.charts?.subscription_plans || "Subscription Plans"}
            </div>
            <p className="text-secondary small mb-4">
              {t.charts?.distribution_by_plan_type ||
                "Distribution by plan type"}
            </p>

            <div
              className="position-relative mx-auto"
              style={{ height: "250px", width: "250px" }}
            >
              <canvas ref={donutChartRef}></canvas>
              <div className="position-absolute top-50 start-50 translate-middle text-center">
                <h4 className="fw-bold mb-0 text-dark">
                  {data?.result?.total_merchants}
                </h4>
                <small className="text-secondary text-center">
                  {t.charts?.total || "Total"}
                </small>
              </div>
            </div>

            <div className="mt-4 text-start">
              {[
                {
                  color: "#9CA3AF",
                  label: t.charts?.free || "Free",
                  value: "10",
                  percent: "100%",
                },
                {
                  color: "#6366F1",
                  label: t.charts?.standard || "Standard",
                  value: "0",
                  percent: "0%",
                },
                {
                  color: "#F97316",
                  label: t.charts?.enterprise || "Enterprise",
                  value: "0",
                  percent: "0%",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="donut-chart-bars d-flex justify-content-between align-items-center mb-2  plan-hover"
                >
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className="rounded-circle d-inline-block"
                      style={{
                        width: "12px",
                        height: "12px",
                        backgroundColor: item.color,
                      }}
                    ></span>
                    <span className="fw-medium text-dark">{item.label}</span>
                  </div>
                  <div className="text-end leading-tight">
                    <div className="text-dark" style={{ marginBottom: "-2px" }}>
                      {item.value}
                    </div>
                    <small
                      style={{
                        color: "#4A5565",
                        fontSize: "12px",
                        fontWeight: "400",
                        marginTop: "0",
                        lineHeight: "1",
                      }}
                    >
                      {item.percent}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MerAnalyticsCharts
